import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, AlertTriangle, ShieldCheck, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const CATEGORY_PREFIX = { DEMO: 'DM', PILOT: 'PL', LIVE: 'SC' };

export const SchoolIdMaintenanceDialog = ({ onClose, onDone }) => {
  const [audit, setAudit] = useState(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState(null);
  const [target, setTarget] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get('/admin/maintenance/school-ids');
      setAudit(data);
    } catch {
      toast.error('Could not read the school id audit');
    }
  };

  useEffect(() => { load(); }, []);

  const nextId = (category) => {
    const prefix = CATEGORY_PREFIX[category];
    const used = (audit?.schools || [])
      .map(s => s.school_id)
      .filter(id => id.startsWith(prefix))
      .map(id => parseInt(id.slice(2), 10));
    const n = used.length ? Math.max(...used) + 1 : 1;
    return `${prefix}${String(n).padStart(4, '0')}`;
  };

  const run = async () => {
    setBusy('migrate');
    try {
      const { data } = await api.post('/admin/maintenance/migrate-school-id', {
        old_school_id: target.school_id,
        new_school_id: nextId('DEMO'),
        category: 'DEMO',
        rebrand_as_demo: true,
        confirm: true,
      });
      setResult(data);
      toast.success(`${data.old_school_id} is now ${data.new_school_id}`);
      await load();
      onDone?.();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'The migration stopped');
    }
    setBusy('');
    setTarget(null);
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="rounded-2xl sm:max-w-2xl max-h-[85vh] overflow-y-auto"
                     data-testid="school-id-maintenance-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Outfit']">School id maintenance</DialogTitle>
          <DialogDescription>
            A demo school is numbered DM, a pilot PL and a live school SC. A school that is on the
            wrong number can be moved here &mdash; everything it owns moves with it, a full backup is
            taken first and the move can be undone.
          </DialogDescription>
        </DialogHeader>

        {!audit ? (
          <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
        ) : (
          <div className="space-y-3">
            {audit.schools.map(s => (
              <div key={s.school_id}
                   className="rounded-xl border border-gray-200 px-4 py-3 flex items-center justify-between gap-3"
                   data-testid={`audit-row-${s.school_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-[#1F2937] truncate">
                    {s.school_id} &middot; {s.school_name}
                  </p>
                  <p className="text-xs text-[#6B7280]">
                    {s.references} references &middot; {s.accounts} accounts &middot; {s.students} students
                    &middot; {s.certificates} certificates
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Badge className="border-0 bg-slate-100 text-slate-600 text-[10px] font-bold">
                    {s.school_category}
                  </Badge>
                  {s.school_id.startsWith('SC') ? (
                    <Button size="sm" variant="outline"
                            onClick={() => setTarget(s)}
                            className="rounded-xl text-[11px] border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid={`migrate-${s.school_id}`}>
                      Move to {nextId('DEMO')} <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  ) : (
                    <Badge className="border-0 bg-green-50 text-green-700 text-[10px]">
                      <ShieldCheck className="h-3 w-3 mr-1" /> correctly numbered
                    </Badge>
                  )}
                </div>
              </div>
            ))}
            <p className="text-[11px] text-[#9CA3AF]">
              Career library: {audit.careers_total} careers ({audit.careers_global} global) &middot;{' '}
              Platform admin: {audit.platform_admins.join(', ') || 'none'} &middot;{' '}
              Integrations: {audit.integrations.join(', ') || 'none'}
            </p>
            {(audit.previous_migrations || []).length > 0 && (
              <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3"
                   data-testid="previous-migrations">
                <p className="text-xs font-semibold text-[#1F2937] mb-1">Backups you can restore</p>
                {audit.previous_migrations.map(m => (
                  <p key={m.stamp} className="text-[11px] text-[#6B7280]">
                    {m.stamp} &middot; {m.school_id} &middot; {m.documents} documents &middot;{' '}
                    {new Date(m.at).toLocaleString()}
                  </p>
                ))}
              </div>
            )}
            {result && (
              <div className="rounded-xl bg-green-50 border border-green-200 px-4 py-3 space-y-1"
                   data-testid="migration-result">
                <p className="text-sm font-semibold text-green-800">
                  {result.old_school_id} &rarr; {result.new_school_id}
                </p>
                <p className="text-xs text-green-800">
                  {result.references_moved} references moved, {result.references_left_behind} left
                  behind. Invite code {result.invite_code}. Certificates rebuilt:{' '}
                  {(result.certificates?.sessions || 0)} experience,{' '}
                  {(result.certificates?.internships || 0)} internship,{' '}
                  {(result.certificates?.recognitions || 0)} recognition. Health check{' '}
                  {result.health.checks_run - result.health.checks_failed}/{result.health.checks_run}.
                </p>
                <p className="text-[11px] text-green-700">
                  Backup {result.backup.stamp} holds {result.backup.documents} documents and can be
                  restored from this screen.
                </p>
              </div>
            )}
          </div>
        )}

        {target && (
          <Dialog open onOpenChange={() => setTarget(null)}>
            <DialogContent className="rounded-2xl sm:max-w-md" data-testid="confirm-migration-dialog">
              <DialogHeader>
                <DialogTitle className="font-['Outfit'] flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500" />
                  Move {target.school_id} to {nextId('DEMO')}?
                </DialogTitle>
                <DialogDescription>
                  All {target.references} references move with it, the invite code is reissued and
                  every certificate is regenerated under the new number. The school becomes
                  &ldquo;Demo International School&rdquo; with its crest and fictional contact
                  details, on DEMO email routing, and every account, student and record is kept.
                  A verified backup is taken first.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTarget(null)} className="rounded-xl">
                  Cancel
                </Button>
                <Button onClick={run} disabled={busy === 'migrate'}
                        className="bg-[#1F2937] text-white hover:bg-[#111827] rounded-xl font-semibold"
                        data-testid="confirm-migration-btn">
                  {busy === 'migrate' ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
                  Yes, move it
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SchoolIdMaintenanceDialog;
